import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import logoHorizontalLight from "../../assets/logos/vissory-logo-horizontal-light.svg";
import "./BackSoon.css";

const EASE = [0.33, 1, 0.68, 1];

const COMMITMENTS = [
  {
    number: "01",
    title: "Pronto, de verdad.",
    text: "Estamos en la recta final, no en el punto de partida. No es una pausa indefinida ni un «ya veremos».",
  },
  {
    number: "02",
    title: "Y bien hecho.",
    text: "Abrimos cuando briefs, propuestas, pagos por etapas y entregas funcionen como tienen que funcionar. Sin parches.",
  },
  {
    number: "03",
    title: "Te avisamos.",
    text: "Lo contaremos en nuestras redes en cuanto abramos. Si tienes algo pendiente con nosotros, escríbenos.",
  },
];

const SOCIALS = [
  { label: "Instagram", href: "https://www.instagram.com/_vissory/" },
  { label: "X", href: "https://x.com/_vissory" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/107349612/" },
];

const TITLE_LINES = [
  { text: "Volvemos pronto.", className: "text-white" },
  { text: "Y volvemos mejor.", className: "text-[#FF5500]" },
];

export default function BackSoon() {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Vissory · Volvemos pronto";
  }, []);

  const appear = (delay, from = {}) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, ...from },
          animate: { opacity: 1, x: 0, y: 0 },
          transition: { duration: 0.9, delay, ease: EASE },
        };

  return (
    <main
      className="back-soon relative flex min-h-screen w-full flex-col overflow-hidden bg-[#0A0A0A] text-white"
      aria-label="Volvemos pronto"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 flex h-screen select-none items-center justify-center overflow-hidden"
        aria-hidden="true"
      >
        <span className="back-soon-watermark font-black uppercase leading-[0.85] text-white/[0.02]">VISSORY</span>
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-[2000px] flex-1 flex-col justify-between gap-10 px-6 pb-10 pt-8 sm:px-12 sm:pb-14 sm:pt-12 md:px-16 lg:px-20 xl:px-24">
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between gap-4">
            <a href="https://vissory.com" aria-label="Vissory">
              <img src={logoHorizontalLight} alt="Vissory" className="h-7 w-auto" />
            </a>
            <p className="flex items-center gap-2.5 rounded-full border border-white/10 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.15em] text-white/60 sm:text-[11px]">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="back-soon-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5500] opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF5500]" />
              </span>
              Trabajando en ello<span className="hidden sm:inline">&nbsp;ahora mismo</span>
            </p>
          </div>

          <div className="flex items-center gap-4">
            <motion.span className="shrink-0 text-xs tracking-wide text-white/40" {...appear(0, { x: -24 })}>
              (01)
            </motion.span>
            <div className="relative h-px min-w-0 flex-1 bg-white/5">
              <motion.div
                className="absolute left-0 top-0 h-full bg-white/10"
                initial={reduceMotion ? false : { width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.6, ease: EASE }}
              />
            </div>
            <motion.span
              className="shrink-0 text-right text-[10px] uppercase tracking-[0.15em] text-white/40 sm:text-[11px]"
              {...appear(2.1)}
            >
              Nueva versión en camino<span className="hidden sm:inline"> — 2026</span>
            </motion.span>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-center py-4 sm:py-8">
          <h1 className="text-[clamp(3rem,10vw,13rem)] font-black leading-[0.95] tracking-tight">
            {TITLE_LINES.map((line, lineIdx) => {
              const words = line.text.split(" ");
              return (
                <span key={line.text} className={`block overflow-hidden ${line.className}`}>
                  {words.map((word, wordIdx) => (
                    <motion.span
                      key={`${word}-${wordIdx}`}
                      className="inline-block"
                      {...appear(0.3 + lineIdx * 0.12 + wordIdx * 0.09, { y: 40 })}
                    >
                      {word}
                      {wordIdx < words.length - 1 ? " " : ""}
                    </motion.span>
                  ))}
                </span>
              );
            })}
          </h1>
        </div>

        <div className="grid gap-10 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:items-end md:gap-16">
          <motion.div {...appear(1.4, { x: -80 })}>
            <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5500]">Qué está pasando</h2>
            <p className="max-w-md text-[15px] leading-relaxed text-white/60">
              Estamos terminando la nueva versión de Vissory y hemos parado la plataforma por poco tiempo para hacerlo
              con calma.
            </p>
            <p className="mt-2 max-w-md text-[15px] leading-relaxed text-white/60">
              No cerramos. Nos ponemos a punto para volver con todo funcionando.
            </p>
            <p className="mt-6 text-sm text-white/50">
              ¿Necesitas algo? Escríbenos a{" "}
              <a
                href="mailto:hello@vissory.com"
                className="text-white/80 underline underline-offset-2 transition-colors hover:text-[#FF5500]"
              >
                hello@vissory.com
              </a>
            </p>
          </motion.div>

          <motion.ol className="border-t border-white/10" {...appear(1.7, { x: 80 })}>
            {COMMITMENTS.map((item) => (
              <li
                key={item.number}
                className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1 border-b border-white/10 py-5 sm:grid-cols-[auto_minmax(0,14rem)_1fr] sm:items-baseline sm:gap-x-8"
              >
                <span className="text-xs tracking-wide text-white/40">({item.number})</span>
                <h3 className="text-lg font-black tracking-tight text-white sm:text-xl">{item.title}</h3>
                <p className="col-start-2 text-[15px] leading-relaxed text-white/50 sm:col-start-3">{item.text}</p>
              </li>
            ))}
          </motion.ol>
        </div>

        <div className="flex flex-col gap-4 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 Vissory</p>
          <nav className="flex items-center gap-6" aria-label="Redes sociales">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-white"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </main>
  );
}
